import React, { useState, useEffect } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { InvestorCard } from '../components/features/Cards';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { investorApi } from '../lib/api';
import { Search, Plus, Users, Building2 } from 'lucide-react';

const InvestorsPage = () => {
  const [loading, setLoading] = useState(true);
  const [investors, setInvestors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ investor_type: '', sectors: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('investor');
  const [newInvestor, setNewInvestor] = useState({
    name: '', firm: '', title: '', investor_type: 'vc', sector_preferences: [], stage_preferences: []
  });
  const [newFund, setNewFund] = useState({
    name: '', description: '', sector_focus: [], stage_focus: []
  });
  const [creating, setCreating] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [investorsRes, fundsRes] = await Promise.all([
        investorApi.list({ query: searchQuery || undefined, ...filters }),
        investorApi.listFunds({ query: searchQuery || undefined })
      ]);
      setInvestors(investorsRes.data.investors || []);
      setFunds(fundsRes.data.funds || []);
    } catch (err) {
      console.error('Failed to fetch investors:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      if (addType === 'investor') {
        const response = await investorApi.create(newInvestor);
        setInvestors([response.data, ...investors]);
      } else {
        const response = await investorApi.createFund(newFund);
        setFunds([response.data, ...funds]);
      }
      setShowAddModal(false);
      setNewInvestor({ name: '', firm: '', title: '', investor_type: 'vc', sector_preferences: [], stage_preferences: [] });
      setNewFund({ name: '', description: '', sector_focus: [], stage_focus: [] });
    } catch (err) {
      console.error('Failed to create:', err);
    } finally {
      setCreating(false);
    }
  };

  const investorTypes = ['vc', 'angel', 'corporate', 'family_office'];
  const sectors = ['AI/ML', 'Fintech', 'SaaS', 'Healthcare', 'Infrastructure', 'Consumer', 'Enterprise'];

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50">Investors</h1>
            <p className="text-zinc-400 font-['Manrope']">Discover and track investors and funds</p>
          </div>
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button data-testid="add-investor-btn" className="bg-white text-zinc-900 hover:bg-zinc-200">
                <Plus className="w-4 h-4 mr-2" />
                Add Investor
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50 font-['Outfit']">Add New</DialogTitle>
              </DialogHeader>
              <Tabs value={addType} onValueChange={setAddType} className="mt-4">
                <TabsList className="bg-zinc-800">
                  <TabsTrigger value="investor" className="data-[state=active]:bg-zinc-700">Investor</TabsTrigger>
                  <TabsTrigger value="fund" className="data-[state=active]:bg-zinc-700">Fund</TabsTrigger>
                </TabsList>
                <form onSubmit={handleCreate} className="mt-4 space-y-4">
                  {addType === 'investor' ? (
                    <>
                      <Input
                        value={newInvestor.name}
                        onChange={(e) => setNewInvestor({ ...newInvestor, name: e.target.value })}
                        placeholder="Investor name"
                        required
                        data-testid="input-investor-name"
                        className="bg-zinc-800 border-zinc-700 text-zinc-50"
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Input
                          value={newInvestor.firm}
                          onChange={(e) => setNewInvestor({ ...newInvestor, firm: e.target.value })}
                          placeholder="Firm"
                          data-testid="input-investor-firm"
                          className="bg-zinc-800 border-zinc-700 text-zinc-50"
                        />
                        <Input
                          value={newInvestor.title}
                          onChange={(e) => setNewInvestor({ ...newInvestor, title: e.target.value })}
                          placeholder="Title"
                          data-testid="input-investor-title"
                          className="bg-zinc-800 border-zinc-700 text-zinc-50"
                        />
                      </div>
                      <Select 
                        value={newInvestor.investor_type} 
                        onValueChange={(v) => setNewInvestor({ ...newInvestor, investor_type: v })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {investorTypes.map((t) => (
                            <SelectItem key={t} value={t} className="text-zinc-50 capitalize">{t.replace('_', ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </>
                  ) : (
                    <>
                      <Input
                        value={newFund.name}
                        onChange={(e) => setNewFund({ ...newFund, name: e.target.value })}
                        placeholder="Fund name"
                        required
                        data-testid="input-fund-name"
                        className="bg-zinc-800 border-zinc-700 text-zinc-50"
                      />
                      <textarea
                        value={newFund.description}
                        onChange={(e) => setNewFund({ ...newFund, description: e.target.value })}
                        placeholder="Description"
                        rows={3}
                        data-testid="input-fund-description"
                        className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-zinc-50 text-sm resize-none"
                      />
                    </>
                  )}
                  <Button 
                    type="submit" 
                    disabled={creating}
                    data-testid="submit-investor-btn"
                    className="w-full bg-white text-zinc-900 hover:bg-zinc-200"
                  >
                    {creating ? 'Creating...' : 'Add'}
                  </Button>
                </form>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search investors and funds..."
              data-testid="investor-search"
              className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-50"
            />
          </div>
          <Select 
            value={filters.investor_type} 
            onValueChange={(v) => setFilters({ ...filters, investor_type: v === 'all' ? '' : v })}
          >
            <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-zinc-50">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              <SelectItem value="all" className="text-zinc-50">All Types</SelectItem>
              {investorTypes.map((t) => (
                <SelectItem key={t} value={t} className="text-zinc-50 capitalize">{t.replace('_', ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </form>

        {/* Content Tabs */}
        <Tabs defaultValue="investors">
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
            <TabsTrigger value="investors" className="data-[state=active]:bg-zinc-800">
              <Users className="w-4 h-4 mr-2" />
              Investors ({investors.length})
            </TabsTrigger>
            <TabsTrigger value="funds" className="data-[state=active]:bg-zinc-800">
              <Building2 className="w-4 h-4 mr-2" />
              Funds ({funds.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="investors">
            {loading ? (
              <div className="grid grid-cols-2 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-md p-4 animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-zinc-800 rounded-full" />
                      <div className="flex-1">
                        <div className="h-4 w-24 bg-zinc-800 rounded mb-2" />
                        <div className="h-3 w-32 bg-zinc-800 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : investors.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {investors.map((investor) => (
                  <InvestorCard key={investor.investor_id} investor={investor} />
                ))}
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-12 text-center">
                <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 font-['Manrope'] mb-4">No investors yet</p>
                <Button onClick={() => setShowAddModal(true)} className="bg-white text-zinc-900 hover:bg-zinc-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Investor
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="funds">
            {loading ? (
              <div className="grid grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-md p-4 animate-pulse">
                    <div className="h-5 w-32 bg-zinc-800 rounded mb-2" />
                    <div className="h-4 w-full bg-zinc-800 rounded" />
                  </div>
                ))}
              </div>
            ) : funds.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {funds.map((fund) => (
                  <div 
                    key={fund.fund_id}
                    data-testid={`fund-card-${fund.fund_id}`}
                    className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer hover:border-zinc-700"
                  >
                    <h3 className="text-zinc-50 font-medium font-['Outfit'] mb-1">{fund.name}</h3>
                    {fund.description && (
                      <p className="text-zinc-400 text-sm font-['Manrope'] line-clamp-2">{fund.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      {fund.sector_focus?.slice(0, 3).map((s, i) => (
                        <span key={i} className="px-2 py-0.5 text-xs font-mono text-zinc-400 bg-zinc-800 rounded-full">{s}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-12 text-center">
                <Building2 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 font-['Manrope'] mb-4">No funds yet</p>
                <Button onClick={() => { setAddType('fund'); setShowAddModal(true); }} className="bg-white text-zinc-900 hover:bg-zinc-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Fund
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default InvestorsPage;
